// Panel lateral contextual del puesto comercial (C2.1 §9): UNA ventana con pestañas Cliente · Cartola
// · Producto · Pedidos · Últimas compras, a toda la altura del área de trabajo. Solo lectura: Comercial
// consulta el resultado de bodega y logística, nunca lo opera (sin picking, packing, staging, camión
// ni movimientos). Lo que el backend aún no tiene se dice como «sin fuente», con su número de GAP.
import { DocumentosNota } from '../dte/DocumentosNota';
import { useDocumentosCxc, useExposicionCxc, useRecibos, type Cartola } from '../tesoreria/datos';
import { api } from '../api/cliente';
import { DescargarCartolaPdf, ImprimirCartolaCliente } from '../tesoreria/Impresos';
import { useNavegar } from '../shell/navegar';
import { useEffect, useRef, useState } from 'react';
import {
  useComunas,
  useCumplimiento,
  useDespachosDeNota,
  useDisponibilidad,
  useDocumentacion,
  useEscalas,
  useNota,
  useNotas,
  usePrecioReferencia,
  useUbicaciones,
  useUltimasCompras,
} from '../api/consultas';
import type {
  CampoFicha,
  FichaEntidad,
  FilaDisponibilidad,
  LineaNota,
  Nota,
} from '../api/contratos';
import { EstadoNota } from '../comun/Estados';
import {
  decimal,
  esCero,
  fecha,
  NOMBRE_CONDICION,
  pesos,
  rut,
  STOCK_POR_REVISAR,
} from '../comun/formato';
import { LineaDeTiempo } from '../consultas/LineaDeTiempo';
import { PanelDocumentoTributario } from '../dte/EstadoDte';
import { useSesion } from '../sesion/SesionContexto';
import { contactoPrincipal, textoDireccion, tramoAplicado } from './comercial';
import { PanelAuditoria, PanelCredito, PanelReserva } from './Paneles';

export type PestanaContexto = 'cliente' | 'cartola' | 'producto' | 'pedidos' | 'compras';

export const PESTANAS_CONTEXTO: ReadonlyArray<{
  id: PestanaContexto;
  nombre: string;
  corto?: string;
  tecla?: string;
}> = [
  { id: 'cliente', nombre: 'Cliente' },
  { id: 'cartola', nombre: 'Cartola', tecla: 'F5' },
  { id: 'producto', nombre: 'Producto' },
  { id: 'pedidos', nombre: 'Pedidos', tecla: 'F6' },
  { id: 'compras', nombre: 'Últimas compras', corto: 'Últ. compras', tecla: 'F7' },
];

type Direccion = FichaEntidad['direcciones'][number];

export interface LineaContexto {
  productoId: string;
  presentacionId: string;
  sku: string;
  nombre: string;
  calculada: LineaNota | null;
}

export interface PropsPanelContexto {
  empresaId: string;
  clienteId: string | null;
  ficha: FichaEntidad | undefined;
  vendedorNombre: string | null;
  listaNombre: string | null;
  listaId: string | null;
  /** Zona logística de la dirección de despacho (la deriva el backend; null = sin zona). */
  zonaNombre?: string | null | undefined;
  ubicacionId: string | null;
  direccion: Direccion | null;
  pestana: PestanaContexto;
  alCambiarPestana: (p: PestanaContexto) => void;
  linea: LineaContexto | null;
  /** Stock de la bodega de la nota ya cargado por la grilla (evita una lectura por producto). */
  stockBodega?: FilaDisponibilidad[] | undefined;
  /** La nota abierta, si ya no es borrador: Pedidos la muestra primero. */
  notaActual?: Nota | undefined;
  alAbrirNota?: ((notaId: string, numero: string) => void) | undefined;
  /** Modo consulta (sin nota): el panel ocupa la ventana completa. */
  completo?: boolean;
  /** Cambia al confirmar la nota: se enfoca su resultado (reserva, crédito, DTE). */
  enfocarResultado?: number;
  /** Ficha 360: zona logística de cada dirección (la deriva el backend). */
  zonaDe?: (direccionId: string) => string | null;
  /** Ficha 360: lo solicitado y aún PENDIENTE de aprobación, por campo (no se aplica todavía). */
  pendientes?: ReadonlyMap<CampoFicha, string>;
  /** Abre la ficha completa del cliente en su pestaña (Alt+F, Rodrigo 2026-10-24). */
  alAbrirFicha?: (() => void) | undefined;
}

/** Lo que el ERP todavía no puede mostrar: se dice, no se inventa. */
export function SinFuente({ gap, children }: { gap: string; children: React.ReactNode }) {
  return (
    <div className="sin-fuente" role="note">
      <span className="insignia tono-neutro">SIN FUENTE · GAP {gap}</span> {children}
    </div>
  );
}

function Dato({
  etiqueta,
  children,
  tono,
}: {
  etiqueta: string;
  children: React.ReactNode;
  tono?: string;
}) {
  return (
    <div className="dato">
      <span className="etiqueta">{etiqueta}</span>
      <span className={`v ${tono ?? ''}`}>{children}</span>
    </div>
  );
}

export function useNombreComuna() {
  const q = useComunas();
  return (codigo: string | null | undefined) =>
    !codigo ? '—' : (q.data?.find((c) => c.codigo === codigo)?.nombre ?? codigo);
}

export function PanelContexto(p: PropsPanelContexto) {
  return (
    <aside
      className={`panel-contexto ${p.completo ? 'completo' : ''}`}
      aria-label="Contexto del cliente"
    >
      <div className="tabs" role="tablist" aria-label="Contexto">
        {PESTANAS_CONTEXTO.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={p.pestana === t.id}
            className={p.pestana === t.id ? 'act' : ''}
            onClick={() => p.alCambiarPestana(t.id)}
            title={t.tecla ? `${t.nombre} (${t.tecla})` : t.nombre}
          >
            {t.corto ? (
              <>
                <span className="largo">{t.nombre}</span>
                <span className="corto">{t.corto}</span>
              </>
            ) : (
              t.nombre
            )}
          </button>
        ))}
      </div>
      {!p.clienteId ? (
        <p className="vacio-panel">Elija el cliente (F2) para ver su contexto.</p>
      ) : p.pestana === 'cliente' ? (
        <VistaCliente {...p} />
      ) : p.pestana === 'cartola' ? (
        <VistaCartola {...p} />
      ) : p.pestana === 'producto' ? (
        <VistaProducto {...p} />
      ) : p.pestana === 'pedidos' ? (
        <VistaPedidos {...p} />
      ) : (
        <VistaCompras {...p} />
      )}
    </aside>
  );
}

// ---------------------------------------------------------------- Cliente

function VistaCliente(p: PropsPanelContexto) {
  const comuna = useNombreComuna();
  const f = p.ficha;
  if (!f) return <p className="vacio-panel">Cargando ficha…</p>;
  const rel = f.empresas.find((e) => e.empresa_id === p.empresaId) ?? null;
  const c = contactoPrincipal(f);
  if (p.completo) return <Ficha360 {...p} />;
  return (
    <div className="vista" data-vista="cliente">
      <div className="tit">
        {f.entidad.razon_social}
        <span className="mono apagado">{rut(f.entidad.rut, f.entidad.dv)}</span>
        {p.alAbrirFicha && (
          <button
            type="button"
            className="boton-ficha"
            onClick={p.alAbrirFicha}
            title="Abrir la ficha completa del cliente (Alt+F)"
          >
            Ficha
          </button>
        )}
      </div>
      <div className="cuerpo">
        <div className="datos-2">
          <Dato etiqueta="Contacto">{c.nombre ?? '—'}</Dato>
          <Dato etiqueta="Teléfono">{c.telefono ?? '—'}</Dato>
          <Dato etiqueta="Dirección despacho">
            {p.direccion ? textoDireccion(p.direccion) : '—'}
          </Dato>
          <Dato etiqueta="Comuna · zona">
            {p.direccion ? comuna(p.direccion.comuna_codigo) : '—'} ·{' '}
            {p.zonaNombre ? (
              p.zonaNombre
            ) : (
              <span className="apagado" title="La comuna no tiene zona logística configurada">
                sin zona
              </span>
            )}
          </Dato>
          <Dato etiqueta="Lista">{p.listaNombre ?? '—'}</Dato>
          <Dato etiqueta="Condición">
            {rel
              ? `${NOMBRE_CONDICION[rel.condicion_pago] ?? rel.condicion_pago}${rel.plazo_dias ? ` ${rel.plazo_dias} días` : ''}`
              : '—'}
          </Dato>
          <Dato etiqueta="Vendedor">{p.vendedorNombre ?? '—'}</Dato>
          <Dato etiqueta="Línea de crédito">
            {rel?.linea_credito ? pesos(rel.linea_credito) : 'sin línea'}
          </Dato>
        </div>
        {rel?.bloqueado && (
          <div className="aviso-linea tono-malo">
            Cliente BLOQUEADO{rel.motivo_bloqueo ? `: ${rel.motivo_bloqueo}` : ''}
          </div>
        )}
        {rel?.observaciones && <div className="aviso-linea tono-info">{rel.observaciones}</div>}
        <TablaDirecciones {...p} />
        <TablaContactos {...p} />
      </div>
    </div>
  );
}

/** El valor y, si hay una solicitud PENDIENTE de ese campo, lo pedido (sin aplicarlo). */
function ConPendiente({
  p,
  campo,
  children,
}: {
  p: PropsPanelContexto;
  campo: CampoFicha;
  children: React.ReactNode;
}) {
  const pedido = p.pendientes?.get(campo);
  return (
    <>
      {children}
      {pedido !== undefined && (
        <span
          className="insignia tono-aviso pendiente-campo"
          data-pendiente={campo}
          title="Cambio solicitado: no se aplica hasta que un supervisor lo apruebe"
        >
          pendiente → {pedido}
        </span>
      )}
    </>
  );
}

/** FICHA COMERCIAL 360: cliente · comercial · direcciones · contactos. */
function Ficha360(p: PropsPanelContexto) {
  const f = p.ficha!;
  const e = f.entidad;
  const rel = f.empresas.find((x) => x.empresa_id === p.empresaId) ?? null;
  const c = contactoPrincipal(f);
  const correo =
    f.medios.find((m) => m.tipo === 'correo' && m.principal) ??
    f.medios.find((m) => m.tipo === 'correo');
  return (
    <div className="vista ficha-360" data-vista="cliente">
      <div className="cuerpo">
        <div className="secciones-360">
          <section aria-label="Cliente">
            <div className="sub-t">Cliente</div>
            <div className="datos-2">
              <Dato etiqueta="RUT">
                <span className="mono">{rut(e.rut, e.dv)}</span>
              </Dato>
              <Dato etiqueta="Estado">
                <span className={`insignia tono-${e.estado === 'activa' ? 'ok' : 'aviso'}`}>
                  {e.estado}
                </span>
              </Dato>
              <Dato etiqueta="Razón social">{e.razon_social}</Dato>
              <Dato etiqueta="Nombre fantasía">{e.nombre_fantasia ?? '—'}</Dato>
              <Dato etiqueta="Giro">{e.giro ?? '—'}</Dato>
              <Dato etiqueta="Teléfono">
                <ConPendiente p={p} campo="telefono">
                  {c.telefono ?? '—'}
                </ConPendiente>
              </Dato>
              <Dato etiqueta="Correo">
                <ConPendiente p={p} campo="email">
                  {correo?.valor ?? '—'}
                </ConPendiente>
              </Dato>
            </div>
          </section>
          <section aria-label="Comercial">
            <div className="sub-t">Comercial</div>
            {rel ? (
              <div className="datos-2">
                <Dato etiqueta="Vendedor">
                  <ConPendiente p={p} campo="vendedor">
                    {p.vendedorNombre ?? 'sin vendedor'}
                  </ConPendiente>
                </Dato>
                <Dato etiqueta="Lista de precios">
                  <ConPendiente p={p} campo="lista_precio">
                    {p.listaNombre ?? 'sin lista'}
                  </ConPendiente>
                </Dato>
                <Dato etiqueta="Condición de pago">
                  <ConPendiente p={p} campo="condicion_pago">
                    {NOMBRE_CONDICION[rel.condicion_pago] ?? rel.condicion_pago}
                  </ConPendiente>
                </Dato>
                <Dato etiqueta="Plazo">{rel.plazo_dias ? `${rel.plazo_dias} días` : '—'}</Dato>
                <Dato etiqueta="Línea de crédito">
                  <ConPendiente p={p} campo="linea_credito">
                    {rel.linea_credito ? pesos(rel.linea_credito) : 'sin línea'}
                  </ConPendiente>
                  <EstadoCxc
                    empresaId={p.empresaId}
                    clienteId={p.clienteId}
                    linea={rel.linea_credito}
                  />
                </Dato>
                <Dato etiqueta="Estado comercial" tono={rel.bloqueado ? 'texto-malo' : ''}>
                  <ConPendiente p={p} campo="estado_comercial">
                    {rel.bloqueado
                      ? `BLOQUEADO${rel.motivo_bloqueo ? `: ${rel.motivo_bloqueo}` : ''}`
                      : 'Activo'}
                  </ConPendiente>
                </Dato>
              </div>
            ) : (
              <p className="apagado">Sin relación comercial con la empresa activa.</p>
            )}
            <div className="dato observaciones">
              <span className="etiqueta">Observaciones</span>
              <span className="v">
                <ConPendiente p={p} campo="observaciones">
                  {rel?.observaciones ?? <span className="apagado">—</span>}
                </ConPendiente>
              </span>
            </div>
          </section>
        </div>
        <TablaDirecciones {...p} />
        <TablaContactos {...p} />
      </div>
    </div>
  );
}

function TablaDirecciones(p: PropsPanelContexto) {
  const comuna = useNombreComuna();
  const f = p.ficha!;
  const nueva = p.pendientes?.get('direccion');
  return (
    <>
      <div className="sub-t">Direcciones</div>
      <table className="grilla-panel" aria-label="Direcciones del cliente">
        {p.completo && (
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Dirección</th>
              <th>Comuna</th>
              <th>Zona</th>
            </tr>
          </thead>
        )}
        <tbody>
          {f.direcciones
            .filter((d) => d.activa)
            .map((d) => (
              <tr key={d.id} className={p.direccion?.id === d.id ? 'sel' : ''}>
                {p.completo && (
                  <td className="apagado">
                    {[
                      d.es_principal ? 'principal' : d.es_despacho ? 'sucursal' : null,
                      d.es_despacho && 'despacho',
                      d.es_tributaria && 'tributaria',
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'otra'}
                    {d.procedencia_estado === 'POR_VALIDAR' && (
                      <span
                        className="insignia tono-neutro"
                        title="Heredada del sistema anterior: provisional"
                      >
                        por validar
                      </span>
                    )}
                  </td>
                )}
                <td>
                  <b>{d.nombre ?? textoDireccion(d)}</b>
                  {d.nombre && <span className="apagado"> · {textoDireccion(d)}</span>}
                </td>
                <td>{comuna(d.comuna_codigo)}</td>
                {p.completo ? (
                  <td>{p.zonaDe?.(d.id) ?? <span className="apagado">sin zona</span>}</td>
                ) : (
                  <td className="apagado">
                    {[
                      d.es_despacho && 'despacho',
                      d.es_principal && 'principal',
                      d.es_tributaria && 'tributaria',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                )}
              </tr>
            ))}
          {nueva !== undefined && (
            <tr data-pendiente="direccion">
              <td colSpan={p.completo ? 4 : 3}>
                <span className="insignia tono-aviso">pendiente</span> {nueva}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function TablaContactos(p: PropsPanelContexto) {
  const f = p.ficha!;
  const nuevo = p.pendientes?.get('contacto');
  const medios = (id: string | null, tipo: string) =>
    f.medios
      .filter((m) => m.contacto_id === id && m.tipo === tipo)
      .map((m) => m.valor)
      .join(' · ');
  return (
    <>
      <div className="sub-t">Contactos</div>
      <table className="grilla-panel" aria-label="Contactos del cliente">
        {p.completo && (
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Correo</th>
            </tr>
          </thead>
        )}
        <tbody>
          {f.contactos
            .filter((k) => k.activo)
            .map((k) => (
              <tr key={k.id}>
                <td>
                  <b>{k.nombre}</b> <span className="apagado">{k.cargo ?? k.area}</span>
                </td>
                {p.completo ? (
                  <>
                    <td>{medios(k.id, 'telefono') || '—'}</td>
                    <td>{medios(k.id, 'correo') || '—'}</td>
                  </>
                ) : (
                  <td>
                    {f.medios
                      .filter((m) => m.contacto_id === k.id)
                      .map((m) => m.valor)
                      .join(' · ') || '—'}
                  </td>
                )}
              </tr>
            ))}
          {p.completo && (medios(null, 'telefono') || medios(null, 'correo')) && (
            <tr>
              <td className="apagado">General</td>
              <td>{medios(null, 'telefono') || '—'}</td>
              <td>{medios(null, 'correo') || '—'}</td>
            </tr>
          )}
          {nuevo !== undefined && (
            <tr data-pendiente="contacto">
              <td colSpan={3}>
                <span className="insignia tono-aviso">pendiente</span> {nuevo}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------- Cartola (cuenta corriente, B4)

/**
 * Deuda conocida del cliente al lado de su línea: «CxC sin fuente» mientras la empresa no tenga su
 * cartera heredada (D-118); después, deuda, vencido y disponible (lo mismo que mira el crédito).
 */
function EstadoCxc({
  empresaId,
  clienteId,
  linea,
}: {
  empresaId: string;
  clienteId: string | null;
  linea: string | null;
}) {
  const { puede } = useSesion();
  const q = useExposicionCxc(empresaId, clienteId, puede('cobranza.ver'));
  const e = q.data?.exposicion;
  if (!e) return <span className="apagado sin-cxc"> · CxC sin fuente</span>;
  const disponible = linea !== null ? Number(linea) - Number(e.deuda) : null;
  return (
    <span className="cxc-conocida" data-deuda={e.deuda}>
      {' '}
      · deuda {pesos(e.deuda)}
      {Number(e.vencidos) > 0 && <b className="tono-malo"> (vencido {pesos(e.vencidos)})</b>}
      {disponible !== null && (
        <>
          {' '}
          · disponible{' '}
          <b className={disponible < 0 ? 'tono-malo' : ''}>{pesos(String(disponible))}</b>
        </>
      )}
    </span>
  );
}

/** Código del documento como en Random (la cartola del panel). */
const TD_PANEL: Record<number, string> = { 33: 'FCV', 34: 'FEV', 39: 'BLV', 41: 'BEV', 56: 'NDV' };

/**
 * La cartola en planilla (Rodrigo, 2026-10-25: «verla desde el punto de venta o la nota para
 * exportarla o imprimirla»): lo pendiente documento a documento, para abrir en Excel.
 */
async function exportarCartola(empresaId: string, clienteId: string, nombre: string) {
  const c = await api.get<Cartola>('/tesoreria/cxc/cartola', {
    query: { empresaId, clienteEntidadId: clienteId },
  });
  const filas = [
    ['Cartola', nombre],
    [],
    ['TD', 'Número', 'NV', 'Emisión', 'Vence', 'Días vencido', 'Valor', 'Saldo', 'Origen'],
    ...(c.pendientes ?? []).map((x) => [
      x.td,
      x.numero,
      x.nv ?? '',
      x.emision,
      x.vence ?? '',
      String(x.diasVencido),
      x.valor,
      x.saldo,
      x.heredado ? 'Random' : 'ERP',
    ]),
    [],
    ['Saldo a favor', c.saldoAFavor],
    ['Saldo', c.saldoFinal],
  ];
  const csv = filas
    .map((f) => f.map((v) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(';'))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `Cartola ${nombre}.csv`;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function VistaCartola(p: PropsPanelContexto) {
  const { puede } = useSesion();
  const [imprimir, setImprimir] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [exportando, setExportando] = useState(false);
  const navegar = useNavegar();
  const nombre = p.ficha?.entidad.razon_social ?? '';
  const ver = puede('cobranza.ver') && !!p.clienteId;
  const expo = useExposicionCxc(p.empresaId, p.clienteId, ver).data?.exposicion ?? null;
  const docs = useDocumentosCxc(
    p.empresaId,
    { clienteEntidadId: p.clienteId ?? undefined, abiertos: true },
    ver,
  );
  const favor = useRecibos(
    p.empresaId,
    { clienteEntidadId: p.clienteId ?? undefined, soloConSaldo: true },
    ver,
  );
  const lista = docs.data ?? [];
  const saldo = lista.reduce((s, d) => s + Number(d.saldo), 0);
  const vencido = lista.filter((d) => d.diasVencido > 0).reduce((s, d) => s + Number(d.saldo), 0);
  const aFavor = (favor.data ?? []).reduce((s, r) => s + Number(r.saldoAFavor), 0);
  return (
    <div className="vista" data-vista="cartola">
      <div className="tit">Cartola · {p.ficha?.entidad.razon_social ?? ''}</div>
      <div className="cuerpo">
        {!puede('cobranza.ver') ? (
          <p className="apagado">
            Ver la cuenta corriente requiere el permiso «Ver cuenta corriente».
          </p>
        ) : (
          <table className="grilla-panel" aria-label="Cartola">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Emisión</th>
                <th>Vence</th>
                <th className="al-der">Saldo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((d) => (
                <tr key={d.documentoId}>
                  <td>
                    {TD_PANEL[d.tipoDte] ?? d.tipoDte} N° {d.folio ?? '—'}
                    {d.notaNumeroVisible === 'Random' && <span className="apagado"> · Random</span>}
                  </td>
                  <td>{fecha(d.fechaEmision)}</td>
                  <td>{fecha(d.fechaVencimiento)}</td>
                  <td className="al-der">{pesos(d.saldo)}</td>
                  <td className={d.diasVencido > 0 ? 'tono-malo' : ''}>
                    {d.diasVencido > 0 ? `vencido ${d.diasVencido} d` : 'al día'}
                  </td>
                </tr>
              ))}
              {docs.isSuccess && !lista.length && (
                <tr>
                  <td colSpan={5} className="apagado">
                    Sin documentos por cobrar
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="pie-panel">
        <span>
          Utilizado <b>{pesos(expo ? expo.deuda : String(saldo))}</b>
        </span>
        <span>
          Vencido{' '}
          <b className={(expo ? Number(expo.vencidos) : vencido) ? 'tono-malo' : ''}>
            {pesos(expo ? expo.vencidos : String(vencido))}
          </b>
        </span>
        <span>
          A favor <b>{pesos(String(aFavor))}</b>
        </span>
      </div>
      {ver && (
        <div className="pie-panel acciones-cartola">
          {navegar && (
            // La cartola completa con todos los movimientos, como en Random (2026-10-25).
            <button
              type="button"
              className="boton-mini"
              onClick={() =>
                navegar('cobros/cartola', { clienteId: p.clienteId!, cliente: nombre })
              }
            >
              Ver movimientos
            </button>
          )}
          <button type="button" className="boton-mini" onClick={() => setImprimir(true)}>
            Imprimir cartola
          </button>
          <button
            type="button"
            className="boton-mini"
            title="Descargar la cartola en PDF, para mandársela al cliente"
            disabled={pdf}
            onClick={() => setPdf(true)}
          >
            {pdf ? 'Preparando…' : 'PDF'}
          </button>
          <button
            type="button"
            className="boton-mini"
            disabled={exportando}
            onClick={() => {
              setExportando(true);
              void exportarCartola(p.empresaId, p.clienteId!, nombre).finally(() =>
                setExportando(false),
              );
            }}
          >
            {exportando ? 'Exportando…' : 'Exportar a Excel'}
          </button>
        </div>
      )}
      {imprimir && p.clienteId && (
        <ImprimirCartolaCliente
          clienteId={p.clienteId}
          nombre={nombre}
          alTerminar={() => setImprimir(false)}
        />
      )}
      {pdf && p.clienteId && (
        <DescargarCartolaPdf
          clienteId={p.clienteId}
          nombre={nombre}
          alTerminar={() => setPdf(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Producto

function VistaProducto(p: PropsPanelContexto) {
  const l = p.linea;
  // Con la nota, el stock de su bodega ya está cargado; sin nota (consulta), se pide por producto.
  const disp = useDisponibilidad(p.empresaId, {
    productoId: p.stockBodega ? null : (l?.productoId ?? null),
  });
  const lista = usePrecioReferencia(
    p.empresaId,
    p.listaId,
    l?.productoId ?? null,
    l?.presentacionId ?? null,
  );
  const escalas = useEscalas(p.empresaId, lista.data?.precioId ?? null);
  const compras = useUltimasCompras(p.empresaId, p.clienteId);
  if (!l)
    return <p className="vacio-panel">Seleccione una línea de la nota para ver su producto.</p>;
  const fila = (p.stockBodega ?? disp.data)?.find(
    (d) => d.productoId === l.productoId && (!p.ubicacionId || d.ubicacionId === p.ubicacionId),
  );
  const c = l.calculada;
  const base = c ? Number(c.cantidadBaseProducto) : 0;
  const aplicado = tramoAplicado(escalas.data ?? [], base);
  const historial = (compras.data ?? []).filter((x) => x.productoId === l.productoId);
  return (
    <div className="vista" data-vista="producto">
      <div className="tit">
        {l.nombre}
        <span className="mono apagado">{l.sku}</span>
      </div>
      <div className="cuerpo">
        <div className="datos-3">
          <Dato etiqueta="Stock físico">{fila ? decimal(fila.fisico) : '—'}</Dato>
          <Dato etiqueta="Reservado" tono="texto-aviso">
            {fila ? decimal(fila.reservado) : '—'}
          </Dato>
          <Dato etiqueta="Disponible" tono={fila?.stockPorRevisar ? 'texto-aviso' : 'texto-ok'}>
            {fila ? decimal(fila.disponible) : '—'}
          </Dato>
        </div>
        {fila?.stockPorRevisar && (
          <p className="insignia tono-aviso" role="alert" data-stock-por-revisar="true">
            {STOCK_POR_REVISAR}.
          </p>
        )}
        <StockPorBodega
          empresaId={p.empresaId}
          productoId={l.productoId}
          resaltar={p.ubicacionId}
        />
        <div className="sub-t">Precio · {p.listaNombre ?? 'lista del cliente'}</div>
        <table className="grilla-panel" aria-label="Precio y escalas">
          <tbody>
            <tr>
              <td>Precio lista (presentación)</td>
              <td className="al-der mono">{lista.data ? pesos(lista.data.valor) : '—'}</td>
            </tr>
            {(escalas.data ?? []).map((t) => (
              <tr key={t.id} className={aplicado?.id === t.id ? 'ap' : ''}>
                <td>
                  Escala desde {decimal(t.cantidad_desde)} u. base
                  {t.cantidad_hasta ? ` hasta ${decimal(t.cantidad_hasta)}` : ''}
                  {aplicado?.id === t.id && <b> · aplicada</b>}
                </td>
                <td className="al-der mono">{pesos(t.valor)}/u</td>
              </tr>
            ))}
            <tr>
              <td>
                <b>Precio venta</b>
              </td>
              <td className="al-der mono fuerte">{c ? pesos(c.precioAplicado) : '—'}</td>
            </tr>
          </tbody>
        </table>
        <div className="sub-t">Últimas compras de este cliente · este producto</div>
        <table className="grilla-panel" aria-label="Últimas compras del producto">
          <thead>
            <tr>
              <th>Fecha</th>
              <th className="al-der">Cantidad</th>
              <th className="al-der">Precio</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((h, i) => (
              <tr key={i}>
                <td>{fecha(h.fecha)}</td>
                <td className="al-der mono">
                  {decimal(h.cantidad)} {h.presentacionCodigo}
                </td>
                <td className="al-der mono">{pesos(h.precioUnitario)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {compras.isSuccess && !historial.length && (
          <p className="apagado">Sin compras de este producto en las últimas notas.</p>
        )}
      </div>
    </div>
  );
}

/** Stock del producto en CADA bodega con saldo (primera etapa: BMA es la fuente principal). */
function StockPorBodega({
  empresaId,
  productoId,
  resaltar,
}: {
  empresaId: string;
  productoId: string;
  resaltar: string | null;
}) {
  const disp = useDisponibilidad(empresaId, { productoId });
  const ubic = useUbicaciones(empresaId);
  const filas = (disp.data ?? []).filter((f) => !esCero(f.fisico) || !esCero(f.reservado));
  if (filas.length < 2) return null;
  const codigo = (id: string) => ubic.data?.find((u) => u.id === id)?.codigo ?? id.slice(0, 6);
  return (
    <>
      <div className="sub-t">Stock por bodega</div>
      <table className="grilla-panel" aria-label="Stock por bodega">
        <thead>
          <tr>
            <th>Bodega</th>
            <th className="al-der">Físico</th>
            <th className="al-der">Reservado</th>
            <th className="al-der">Disponible</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.ubicacionId} className={f.ubicacionId === resaltar ? 'sel' : ''}>
              <td>
                {codigo(f.ubicacionId)}
                {f.stockPorRevisar && <span className="insignia tono-aviso">revisar</span>}
              </td>
              <td className="al-der mono">{decimal(f.fisico)}</td>
              <td className="al-der mono">{decimal(f.reservado)}</td>
              <td className="al-der mono fuerte">{decimal(f.disponible)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------- Pedidos / seguimiento (solo lectura)

function VistaPedidos(p: PropsPanelContexto) {
  const notas = useNotas(p.empresaId, { clienteEntidadId: p.clienteId ?? undefined, pagina: 1 });
  const actual = p.notaActual && p.notaActual.estado !== 'BORRADOR' ? p.notaActual : undefined;
  // La nota abierta manda; otro pedido solo si la persona lo elige a mano.
  const [sel, setSel] = useState<string | null>(null);
  const items = (notas.data?.items ?? []).filter((n) => n.estado !== 'BORRADOR').slice(0, 8);
  const elegida = sel ?? actual?.notaId ?? items[0]?.id ?? null;
  return (
    <div className="vista" data-vista="pedidos">
      <div className="tit">
        Pedidos · {p.ficha?.entidad.razon_social ?? ''}
        <span className="apagado">solo lectura</span>
      </div>
      <div className="cuerpo">
        <table className="grilla-panel" aria-label="Pedidos del cliente">
          <thead>
            <tr>
              <th>NV</th>
              <th>Fecha</th>
              <th>Documento</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((n) => (
              <tr
                key={n.id}
                className={n.id === elegida ? 'sel' : ''}
                tabIndex={0}
                onClick={() => setSel(n.id)}
                onDoubleClick={() => p.alAbrirNota?.(n.id, n.numeroVisible)}
                onKeyDown={(e) => e.key === 'Enter' && setSel(n.id)}
              >
                <td className="mono">{n.venta_directa ? 'Directa' : n.numeroVisible}</td>
                <td>{fecha(n.fecha_negocio)}</td>
                <td>
                  <DocumentosNota documentos={n.documentos} vacio="por emitir" />
                </td>
                <td>
                  <EstadoNota estado={n.estado} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {notas.isSuccess && !items.length && (
          <p className="apagado">El cliente no tiene pedidos.</p>
        )}
        {elegida && (
          <Seguimiento
            empresaId={p.empresaId}
            notaId={elegida}
            esActual={elegida === actual?.notaId}
            enfocar={p.enfocarResultado ?? 0}
          />
        )}
      </div>
    </div>
  );
}

function Seguimiento({
  empresaId,
  notaId,
  esActual,
  enfocar,
}: {
  empresaId: string;
  notaId: string;
  esActual: boolean;
  enfocar: number;
}) {
  const { puede } = useSesion();
  const nota = useNota(empresaId, notaId);
  const n = nota.data;
  const resultado = useRef<HTMLDivElement>(null);
  const listo = esActual && !!n && n.estado !== 'BORRADOR';
  useEffect(() => {
    if (enfocar && listo) resultado.current?.focus();
  }, [enfocar, listo]);
  const confirmada = !!n && ['CONFIRMADA', 'CERRADA_CORTA'].includes(n.estado);
  const cumpl = useCumplimiento(empresaId, confirmada ? notaId : null);
  const desp = useDespachosDeNota(empresaId, confirmada ? notaId : null);
  const doc = useDocumentacion(empresaId, notaId, confirmada && puede('dte.ver'));
  if (!n) return <p className="apagado">Cargando pedido…</p>;
  const despachos = desp.data?.items ?? [];
  const confirmados = despachos.filter((d) => d.estado === 'CONFIRMADO');
  const docs = doc.data?.documentos ?? [];
  return (
    <div className="seguimiento" data-seguimiento={n.numeroVisible}>
      <div className="sub-t">
        Resumen · {n.numeroVisible} · {fecha(n.cabecera.fechaNegocio)} · {pesos(n.totales.bruto)}
      </div>
      <div className="datos-4">
        <Dato etiqueta="Estado">
          <EstadoNota estado={n.estado} />
        </Dato>
        <Dato etiqueta="Reserva">
          {confirmada
            ? esCero(n.reservas.pendiente)
              ? 'completa'
              : `parcial · pend. ${decimal(n.reservas.pendiente)}`
            : '—'}
        </Dato>
        <Dato etiqueta="Preparación">
          <span className="apagado" title="No existe el registro de preparación (GAP 14b)">
            sin fuente
          </span>
        </Dato>
        <Dato etiqueta="Despachos">
          {confirmada ? `${confirmados.length} de ${despachos.length || 0}` : '—'}
        </Dato>
        <Dato etiqueta="Ruta">
          {confirmados.some((d) => d.modalidad === 'RUTA') ? (
            'en ruta'
          ) : (
            <span className="apagado" title="No hay entidad de ruta (GAP 14b)">
              sin fuente
            </span>
          )}
        </Dato>
        <Dato etiqueta="Zona">
          {n.cabecera.zonaLogisticaNombre ?? (
            <span className="apagado" title="La dirección de despacho no tiene zona configurada">
              sin zona
            </span>
          )}
        </Dato>
        <Dato etiqueta="Entrega">
          <span className="apagado" title="No hay confirmación de entrega (GAP 14b)">
            sin fuente
          </span>
        </Dato>
        <Dato etiqueta="Documento">
          {docs.length
            ? docs
                .map((d) =>
                  d.dte ? `${d.dte.tipoDte} ${d.dte.folio ?? 's/folio'}` : d.tipoDocumento,
                )
                .join(' · ')
            : '—'}
        </Dato>
      </div>
      <div className="sub-t">Detalle de líneas</div>
      <table className="grilla-panel" aria-label="Detalle de líneas del pedido">
        <thead>
          <tr>
            <th>Producto</th>
            <th className="al-der">Pedido</th>
            <th className="al-der">Entregado</th>
            <th className="al-der">Saldo</th>
          </tr>
        </thead>
        <tbody>
          {(cumpl.data?.lineas ?? []).map((c) => {
            const l = n.lineas.find((x) => x.numero === c.numero);
            const saldo = !esCero(c.pendienteDespacho);
            return (
              <tr key={c.numero} className={saldo ? 'pend' : ''}>
                <td title={l?.sku}>{l?.descripcion ?? `línea ${c.numero}`}</td>
                <td className="al-der mono">{decimal(c.confirmado)}</td>
                <td className="al-der mono">{decimal(c.despachado)}</td>
                <td className={`al-der mono ${saldo ? 'saldo' : 'apagado'}`}>
                  {decimal(c.pendienteDespacho)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!confirmada && <p className="apagado">El pedido aún no está confirmado: no hay despacho.</p>}
      {esActual && (
        <div className="esta-nota" ref={resultado} tabIndex={-1} aria-label="Resultado de la nota">
          <PanelCredito nota={n} />
          <PanelReserva nota={n} />
          <PanelDocumentoTributario nota={n} />
          <LineaDeTiempo nota={n} />
          <PanelAuditoria nota={n} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Últimas compras (§8)

function VistaCompras(p: PropsPanelContexto) {
  const compras = useUltimasCompras(p.empresaId, p.clienteId);
  return (
    <div className="vista" data-vista="compras">
      <div className="tit">Últimas compras · {p.ficha?.entidad.razon_social ?? ''}</div>
      <div className="cuerpo">
        <table className="grilla-panel" aria-label="Últimas compras">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Producto</th>
              <th className="al-der">Cant.</th>
              <th className="al-der">Precio</th>
              <th>Doc.</th>
            </tr>
          </thead>
          <tbody>
            {(compras.data ?? []).map((h, i) => (
              <tr key={i}>
                <td>{fecha(h.fecha)}</td>
                <td title={h.sku}>{h.descripcion}</td>
                <td className="al-der mono">
                  {decimal(h.cantidad)} {h.presentacionCodigo}
                </td>
                <td className="al-der mono">{pesos(h.precioUnitario)}</td>
                <td>
                  <DocumentosNota documentos={h.documentos} vacio={h.numeroVisible} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {compras.isSuccess && !compras.data.length && (
          <p className="apagado">Sin compras confirmadas.</p>
        )}
        <p className="nota-gap">
          Notas comprometidas de esta empresa, la más reciente primero. «Agregar de nuevo a la NV»
          queda pendiente.
        </p>
      </div>
    </div>
  );
}
